import { useState } from "react";
import { updateUrl, setLoadListener, applySymbolStyleWithOpacity, applyOpacityToPoints, getDataset } from "../utils/mapFunctions";

const LayerControl = (props) => {
  const map = props.map;
  const layers = props.layers;
  const [copied, setCopied] = useState(null);

  const toggleVisibility = (layerId) => {
    props.setLayers(prevLayers => {
      const newLayers = prevLayers.map(layer => {
        if (layer.id === layerId) {
          const leaf = layer.layer;

          if (layer.visibility) {
            if (map.hasLayer(leaf)) {
              map.removeLayer(leaf);
            }
            return { ...layer, visibility: false };
          } else {

            if (!map.hasLayer(leaf)) {
              const nextLayer = prevLayers
                .filter(l => l.visibility && l.id !== layer.id && map.hasLayer(l.layer) && l.order > layer.order)
                .sort((a, b) => a.order - b.order)[0];

              if (nextLayer) {
                map.addLayer(leaf);
                map.removeLayer(nextLayer.layer);
                map.addLayer(nextLayer.layer);
              } else {
                map.addLayer(leaf);
              }

              if (leaf._symbol) {
                applySymbolStyleWithOpacity(leaf, leaf._symbol, layer.opacity);
              } else if (typeof leaf.setOpacity === "function") {
                leaf.setOpacity(layer.opacity);
              } else if (typeof leaf.setStyle === "function") {
                try {
                  leaf.setStyle(() => ({
                    fillOpacity: layer.opacity,
                    opacity: layer.opacity
                  }));
                } catch (err) {
                  console.warn("Could not apply style to layer " + layer.id, err);
                }
              }

              setLoadListener({ layer: leaf }, prevLayers);
            }

            return { ...layer, visibility: true };
          }
        }
        return layer;
      });

      setTimeout(() => {
        const visibleLayers = newLayers.filter(l => l.visibility && map.hasLayer(l.layer))
          .sort((a, b) => a.order - b.order);

        visibleLayers.forEach((layer, index) => {
          if (typeof layer.layer.setZIndex === "function") {
            layer.layer.setZIndex(index);
          }
        });
      }, 20);

      return newLayers;
    });
  };

  const reorderLayers = (layersToUse) => {
    const visibleLayers = layersToUse
      .filter(l => l.visibility && map.hasLayer(l.layer))
      .sort((a, b) => a.order - b.order);

    visibleLayers.forEach((layer, index) => {
      if (typeof layer.layer.setZIndex === "function") {
        layer.layer.setZIndex(index);
      }
    });
  };

  const changeOrder = (layerId, direction) => {
    props.setLayers(prevLayers => {
      const sortedLayers = [...prevLayers].sort((a, b) => a.order - b.order);
      const index = sortedLayers.findIndex(l => l.id === layerId);
      if (index === -1) return prevLayers;

      const swapWith = direction === "up" ? index + 1 : index - 1;

      if (swapWith < 0 || swapWith >= sortedLayers.length) return prevLayers;

      const tempOrder = sortedLayers[index].order;
      sortedLayers[index].order = sortedLayers[swapWith].order;
      sortedLayers[swapWith].order = tempOrder;

      const reorderedLayers = sortedLayers.sort((a, b) => a.order - b.order);

      setTimeout(() => {
        reorderLayers(reorderedLayers);
        const orderedIds = reorderedLayers.map(l => l.id);
        updateUrl(orderedIds);
      }, 10);

      return reorderedLayers;
    });
  };

  const updateLayerOpacity = (layerId, newOpacity) => {
    props.setLayers(prevLayers =>
      prevLayers.map(layerItem => {
        if (layerItem.id === layerId) {
          const leaf = layerItem.layer;

          if (!leaf) {
            console.warn("Layer not found for id " + layerId);
            return layerItem;
          }

          if (leaf._isUnesco) {
            applyOpacityToPoints(leaf, newOpacity);
          } else if (leaf._symbol) {
            applySymbolStyleWithOpacity(leaf, leaf._symbol, newOpacity);
          } else if (typeof leaf.setOpacity === "function") {
            leaf.setOpacity(newOpacity);
          } else if (typeof leaf.setStyle === "function") {
            applyOpacityToPoints(leaf, newOpacity);
          } else {
            console.warn("Layer opacity can't be changed " + layerId);
          }

          setLoadListener({ layer: leaf}, layers);

          return { ...layerItem, opacity: newOpacity };
        }
        return layerItem;
      })
    );
  };

  const handleRemoveLayer = (layerToRemove) => {
    if (map.hasLayer(layerToRemove.layer)) {
      map.removeLayer(layerToRemove.layer);
    }
    props.removeLayer(layerToRemove);
    updateUrl(layerToRemove.id);

    setTimeout(() => {
      reorderLayers(props.layers);
    }, 10);
  };

  const copyURL = async (id) => {
    let url = getDataset(id).GetCapabilities;
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopied(id);
        setTimeout(() => {
          setCopied(null);
        }, 2000);
      })
      .catch((err) => console.error("Error copying the URL:", err));
  }

  return (
    <>
      {
        props.active &&
        <div className="layer-container">
          {layers.length ? layers.slice().sort((a,b) => b.order - a.order).map((layer, i) => (
            <div className="layer-item" key={i}>
              <div className="layer-header">
                <div className="layer-title">
                  {layer.title}
                </div>
                <div className="layer-buttons">
                  <button className="icon-button layer-copy" onClick={() => copyURL(layer.id)} disabled={typeof layer.id === 'string' && layer.id.startsWith("ext-")} aria-label="Copy capabilities">
                    {copied === layer.id ? <i className="fa-solid fa-check"></i> : <i className="fa-solid fa-clone"></i>}
                  </button>
                  <button className="icon-button" onClick={() => props.setShowDetail(layer.id)} disabled={typeof layer.id === 'string' && layer.id.startsWith("ext-")} aria-label="Dataset details">
                    <i className="fa-solid fa-circle-info"></i>
                  </button>
                  <button className="icon-button" onClick={() => handleRemoveLayer(layer)} aria-label="Remove layer">
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
              <div className="layer-settings">
                <div className="layer-order">
                  <button className="icon-button" disabled={layer.order === layers.length - 1} onClick={() => changeOrder(layer.id, "up")} aria-label="Move layer up">
                    <i className="fa-solid fa-chevron-up"></i>
                  </button>
                  <button className="icon-button" disabled={layer.order === 0} onClick={() => changeOrder(layer.id, "down")} aria-label="Move layer down">
                    <i className="fa-solid fa-chevron-down"></i>
                  </button>
                </div>
                <div className="layer-opacity">
                  <input
                    className="custom-range"
                    type="range"
                    min="0"
                    max="100"
                    value={layer.opacity * 100}
                    style={{ "--range-progress": layer.opacity * 100 + "%" }}
                    onChange={(e) => updateLayerOpacity(layer.id, e.target.value / 100)}
                  />
                </div>
                <button className="icon-button layer-visibility" onClick={() => toggleVisibility(layer.id)} aria-label={layer.visibility ? "Hide layer" : "Show layer"}>
                  <i className={"fa-solid " + (layer.visibility ? "fa-eye" : "fa-eye-slash")} style={{ width: "1.25rem" }}></i>
                </button>
              </div>
            </div>
          )) : <div className="widget-text"><div>The map is empty.</div>Explore the <span className="icon-button"><i className="fa-solid fa-book"></i></span> Cataloge to add layers.</div>}
        </div>
      }
    </>
  );
};

export default LayerControl;
